import cx from 'classnames';
import {DateTime} from 'luxon';
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  TimePickerWheel,
  TimePickerWheelConfig,
} from '~/common/components/TimePickerWheel/TimePickerWheel';
import {parseTime} from '~/common/utils';
import {Button} from '~library/atoms/Button';
import {DatePicker} from '~library/atoms/DatePicker';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {CustomTime} from './CustomTime';
import {StartEndControllerTab} from './types';

interface DateTimeControlProps {
  selectedTab: StartEndControllerTab;
  onChangeDate: (date: Date) => void;
  onChangeTime: (time: string) => void;
  selectedDateTime: DateTime | null;
  minDate: DateTime | null;
  maxDate?: DateTime;
  times: string[];
  open?: boolean;
  qaIdPrefix?: string;
}

export const DateTimeControl: FC<DateTimeControlProps> = ({
  selectedTab,
  onChangeDate,
  onChangeTime,
  selectedDateTime,
  minDate,
  maxDate,
  times,
  open = false,
  qaIdPrefix = 'event',
}) => {
  const selectedDate = selectedDateTime?.toFormat('yyyy-MM-dd');
  const selectedTime = selectedDateTime?.toFormat('h:mm a');
  const timesContainerRef = useRef<HTMLDivElement>(null);
  const isMedium = useIsMediumAndUp();
  const [isCustomTimeInputOpen, setIsCustomTimeInputOpen] = useState(false);

  const timeOptions = useMemo(() => {
    const minDateTime = minDate ?? DateTime.now();
    const selectedDT = selectedDateTime ?? DateTime.now();
    return times.map((time) => {
      // No selected date/time; all options are enabled
      if (!selectedDT) return {time, disabled: false};

      const match = parseTime(time);
      // If invalid datetime format, disable the option
      if (!match?.groups) return {time, disabled: true};

      const {minute = '00', meridiem}: TimePickerWheelConfig = match.groups;
      let {hour}: TimePickerWheelConfig = match.groups;
      if (hour === '12' && meridiem === 'AM') hour = '00'; // Handle 12 AM case
      let hourNum = parseInt(hour, 10);
      if (meridiem === 'PM' && hourNum !== 12) hourNum += 12; // Convert PM hour to 24-hour format
      const dateTime = selectedDT.set({
        hour: hourNum,
        minute: parseInt(minute, 10),
      });

      const isBeforeMin = dateTime.diff(minDateTime, 'minutes').minutes < 0;
      const isAfterMax =
        maxDate?.hasSame(selectedDT, 'day') && dateTime.toMillis() >= maxDate.toMillis();

      return {time, disabled: isBeforeMin || isAfterMax};
    });
  }, [times, minDate, maxDate, selectedDateTime]);

  useEffect(() => {
    if (!open || !timesContainerRef.current || !isMedium) return;
    let selectedTimeElement = timesContainerRef.current.querySelector(
      `[data-qa-value="${selectedTime}"]`
    );
    if (selectedTimeElement) {
      // Scroll to selected time option
      selectedTimeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    } else {
      // No time selected; scroll to first possible time option for today
      selectedTimeElement = timesContainerRef.current.querySelector(`:not([disabled])`);
      selectedTimeElement?.scrollIntoView({
        block: 'start',
        inline: 'center',
      });
    }
  }, [open, selectedTime, isMedium]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!timesContainerRef.current) return;
    const selectedTimeElement = timesContainerRef.current.querySelector(
      `[data-qa-value="${selectedTime}"]`
    );
    if (!selectedTimeElement) return;
    selectedTimeElement.scrollIntoView({
      behavior: 'instant',
      block: 'center',
      inline: 'center',
    });
  }, [open, selectedTab]);

  const openCustomTimeInput = useCallback(() => {
    setIsCustomTimeInputOpen(true);
  }, []);

  const closeCustomTimeInput = useCallback(() => {
    setIsCustomTimeInputOpen(false);
  }, []);

  const handleChangeDate = useCallback(
    (dates: Date[]) => {
      if (dates.length === 0) return; // No dates returned from DatePicker; skip processing
      onChangeDate(dates[0]);
    },
    [onChangeDate]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setIsCustomTimeInputOpen(false);
  }, [isMedium]);

  useEffect(() => {
    const mobileTimePicker = document.querySelector('.start-end-controller__mobile-time-picker');
    if (!mobileTimePicker) return undefined;

    const disableParentScroll = () => {
      const parent = document.getElementById('start-end-controller');
      if (!parent) return;
      parent.style.overflow = 'hidden';
    };

    const enableParentScroll = () => {
      const parent = document.getElementById('start-end-controller');
      if (!parent) return;
      parent.style.overflow = 'auto';
    };

    mobileTimePicker.addEventListener('touchstart', disableParentScroll);
    mobileTimePicker.addEventListener('touchend', enableParentScroll);
    return () => {
      mobileTimePicker.removeEventListener('touchstart', disableParentScroll);
      mobileTimePicker.removeEventListener('touchend', enableParentScroll);
    };
  });

  return (
    <div className="start-end-controller__datetime-wrapper">
      <DatePicker
        inline
        id={selectedTab === 'end' ? `${qaIdPrefix}-end-date` : `${qaIdPrefix}-start-date`}
        name="date"
        data-qa-id={`${qaIdPrefix}-date`}
        value={selectedDate}
        onChange={handleChangeDate}
        minDate={minDate?.toFormat('yyyy-MM-dd')}
        maxDate={maxDate?.toFormat('yyyy-MM-dd')}
      />
      <div className="start-end-controller__time-picker-wrapper">
        {isMedium ? (
          <>
            <Typography
              variant="paragraph2"
              semibold
              className="start-end-controller__desktop-time-picker__title"
            >
              {selectedTab === 'start' ? 'Start Time' : 'End Time'}
            </Typography>
            <div
              className="start-end-controller__desktop-time-picker"
              data-qa-id="start-end-controller__time-picker"
              ref={timesContainerRef}
            >
              {timeOptions.map(({time, disabled}) => (
                <Button
                  variant="unstyled-button"
                  className={cx('start-end-controller__desktop-time-option', {
                    selected: selectedTime === time,
                  })}
                  key={time}
                  onClick={() => onChangeTime(time)}
                  data-qa-id={`${qaIdPrefix}-time-option`}
                  data-qa-value={time}
                  disabled={disabled}
                  noTextWrap
                >
                  {time}
                </Button>
              ))}
              <Divider marginLeft="0.5rem" marginRight="0.5rem" width="calc(100% - 1rem)" />
              <Button
                variant="unstyled-button"
                className={cx('start-end-controller__desktop-time-option', {
                  selected: selectedTime && !times.some((t) => t === selectedTime),
                })}
                onClick={openCustomTimeInput}
                data-qa-id={`${qaIdPrefix}-custom-time-trigger`}
                data-qa-value={!times.some((t) => t === selectedTime) ? selectedTime : undefined}
                noTextWrap
              >
                Custom time
              </Button>
            </div>
            <CustomTime
              open={isCustomTimeInputOpen}
              time={selectedTime || ''}
              onChange={onChangeTime}
              onClose={closeCustomTimeInput}
            />
          </>
        ) : (
          <TimePickerWheel
            selectedTime={selectedTime}
            onChange={onChangeTime}
            data-qa-id="start-end-controller__time-picker"
          />
        )}
      </div>
    </div>
  );
};
